import { privateRequest } from "../axios";
import type { ApiResponse, ListApiResponse } from "@/types/common";

// Real endpoint, confirmed against a live authenticated response given
// directly by the user: `GET /admin/roles`.
export interface Role {
  id: number;
  name: string;
  description: string | null;
}

export interface GetRolesParams {
  pageNo?: number;
  perPage?: number;
}

export interface CreateRolePayload {
  name: string;
  description?: string | null;
}

export interface RolePermissionItem {
  permissionId: number;
  permissionName: string;
  hasPermission: boolean;
}

export interface RoleModulePermissions {
  moduleId: number;
  moduleName: string;
  permissions: RolePermissionItem[];
}

export interface UpdateRolePayload {
  name: string;
  description?: string | null;
  permissionIds: number[];
}

// Real endpoint, confirmed against the Swagger UI response schema given
// directly by the user: `GET /admin/roles/permissions-matrix`.
export type PermissionAccessLevel = "FULL_ACCESS" | "VIEW_ONLY" | "NO_ACCESS";

export interface RoleModule {
  id: number;
  name: string;
}

export interface RolePermission {
  moduleId: number;
  moduleName: string;
  accessLevel: PermissionAccessLevel;
}

export interface RoleWithPermissions {
  roleId: number;
  roleName: string;
  isDefault: boolean;
  permissions: RolePermission[];
}

export interface RolePermissionsMatrixData {
  modules: RoleModule[];
  roles: RoleWithPermissions[];
}

const ENDPOINTS = {
  ROLES: "/roles",
  ROLE_PERMISSIONS_MATRIX: "/roles/permissions-matrix",
  ROLE_BY_ID: (id: number) => `/roles/${id}`,
  ROLE_PERMISSIONS_BY_ROLE_ID: (roleId: number) => `/roles/${roleId}/permissions`,
};

const getRoles = async (params?: GetRolesParams) => {
  const response = await privateRequest.get<ListApiResponse<Role>>(ENDPOINTS.ROLES, { params });
  return response.data;
};

const getRolePermissionsMatrix = async () => {
  const response = await privateRequest.get<ApiResponse<RolePermissionsMatrixData>>(ENDPOINTS.ROLE_PERMISSIONS_MATRIX);
  return response.data;
};

const createRole = async (payload: CreateRolePayload) => {
  const response = await privateRequest.post<ApiResponse<Role>>(ENDPOINTS.ROLES, payload);
  return response.data;
};

const getRolePermissionsByRoleId = async (roleId: number) => {
  const response = await privateRequest.get<ApiResponse<RoleModulePermissions[]>>(
    ENDPOINTS.ROLE_PERMISSIONS_BY_ROLE_ID(roleId),
  );
  return response.data;
};

const updateRole = async (id: number, payload: UpdateRolePayload) => {
  const response = await privateRequest.put<ApiResponse<Role>>(ENDPOINTS.ROLE_BY_ID(id), payload);
  return response.data;
};

const deleteRole = async (id: number) => {
  const response = await privateRequest.delete<ApiResponse<unknown>>(ENDPOINTS.ROLE_BY_ID(id));
  return response.data;
};

const roleService = {
  getRoles,
  getRolePermissionsMatrix,
  createRole,
  getRolePermissionsByRoleId,
  updateRole,
  deleteRole,
};

export default roleService;
